import type { FindCaseRecordsRequest } from '@compass/client/types/case_record';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { exportCaseRecords, exportCasesForCampaign } from '@/services/export.service';
import { useStore } from '@/store/index';
import type { CallsExportOption, ExportFormat } from '@/types/export.types';

export const useDownloadCaseRecords = () => {
  const [isExporting, setIsExporting] = useState(false);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const context = useMemo(
    () => ({
      organizationId: selectedOrganizationId,
      programId: selectedProgramId,
    }),
    [selectedOrganizationId, selectedProgramId],
  );

  const downloadCaseRecords = async (
    programId: string,
    filters?: Omit<FindCaseRecordsRequest, 'limit' | 'offset'>,
    format: 'csv' | 'xlsx' = 'csv',
    includeEligibilityChecks: boolean = false,
    customFilename?: string, // Optional custom filename for campaigns
    callsOption: CallsExportOption = 'none',
    workflowType?: string,
  ): Promise<void> => {
    if (!programId) {
      toast.error('Program ID is required');
      return;
    }

    if (!context.organizationId || !context.programId) {
      toast.error('Organization and program context is required');
      return;
    }

    setIsExporting(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);

    try {
      await exportCaseRecords({
        programId,
        organizationId: context.organizationId,
        filters,
        options: {
          format: format as ExportFormat,
          filename: customFilename,
          includeEligibilityChecks,
          sheetName: 'Case Records',
          callsOption,
          workflowType,
        },
        onProgress: progress => {
          toast.loading(progress.message, { id: toastId });
        },
      });

      toast.success(`Case records exported as ${format.toUpperCase()}`, { id: toastId });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export case records', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Downloads cases as a campaign-uploadable CSV (schema field-id headers,
   * `|`-joined repeated values) so the file round-trips through the bulk-create
   * campaign upload — unlike downloadCaseRecords' label-header reporting export.
   */
  const downloadCasesForCampaign = async (
    workflowId: string,
    caseRecordIds: string[],
    filename: string,
    format: 'csv' | 'xlsx' = 'csv',
  ): Promise<void> => {
    if (!context.organizationId || !context.programId) {
      toast.error('Organization and program context is required');
      return;
    }

    setIsExporting(true);
    const label = format.toUpperCase();
    const toastId = toast.loading(`Preparing campaign ${label}...`);

    try {
      await exportCasesForCampaign({
        organizationId: context.organizationId,
        programId: context.programId,
        workflowId,
        caseRecordIds,
        filename,
        format,
      });
      toast.success(`Campaign ${label} downloaded`, { id: toastId });
    } catch (error) {
      console.error('Campaign export failed:', error);
      toast.error(`Failed to export campaign ${label}`, { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    downloadCaseRecords,
    downloadCasesForCampaign,
    isExporting,
  };
};
